'use client';
import { Box } from '@mui/material';
import { useMemo } from 'react';
import type { StateAbbreviation } from '@repo/supergraph/marketplace/types';
import type {
  CategorySection as CategorySectionType,
  ProductCard as ProductCardType,
} from '@repo/supergraph/roundup/base/types';
import CategorySection from './CategorySection';
import {
  createOfferOrderMap,
  filterProductCardsByOffers,
  sortProductCardsByOfferOrder,
  sortSectionsByFirstCardOrder,
} from './utils';

type ProductCardCategorySectionsProps = {
  categorySections: CategorySectionType[];
  isSeoBankingFFActive: boolean;
  regionCode?: StateAbbreviation;
  showSkeleton?: boolean;
  offersListToFilterWith?: ProductCardType[];
  isLoading?: boolean;
  isSorted?: boolean;
  slug?: string;
  shouldApplyStylingUpdates?: boolean;
  isFilterEnabled?: boolean;
};

export default function ProductCardCategorySections({
  categorySections,
  isSeoBankingFFActive,
  regionCode,
  showSkeleton = false,
  offersListToFilterWith,
  isLoading,
  isSorted = false,
  shouldApplyStylingUpdates = false,
  isFilterEnabled = false,
}: ProductCardCategorySectionsProps) {
  let productPositionBase = -1;

  const filteredSections = useMemo(() => {
    if (!isFilterEnabled || !offersListToFilterWith) {
      return categorySections;
    }

    const offerOrderMap = createOfferOrderMap(offersListToFilterWith);

    const sectionsWithProcessedCards = categorySections
      .map(section => {
        const filteredCards = filterProductCardsByOffers(
          section.productCards,
          offersListToFilterWith,
        );

        return {
          ...section,
          productCards: isSorted
            ? sortProductCardsByOfferOrder(filteredCards, offerOrderMap)
            : filteredCards,
        };
      })
      .filter(section => section.productCards.length > 0);

    return isSorted
      ? sortSectionsByFirstCardOrder(sectionsWithProcessedCards, offerOrderMap)
      : sectionsWithProcessedCards;
  }, [categorySections, offersListToFilterWith, isSorted, isFilterEnabled]);

  return (
    <Box sx={{ marginBottom: 8, p: 0 }} component="ul">
      {filteredSections.map((section, sectionIndex) => {
        productPositionBase += section.productCards?.length || 0;
        return (
          <CategorySection
            key={sectionIndex}
            title={section.title}
            description={section.description}
            eyebrowText={section.eyebrowText}
            productCards={section.productCards}
            productPositionBase={productPositionBase}
            isSeoBankingFFActive={isSeoBankingFFActive}
            regionCode={regionCode}
            showSkeleton={showSkeleton || isLoading}
            shouldApplyStylingUpdates={shouldApplyStylingUpdates}
          />
        );
      })}
    </Box>
  );
}
